import { useState } from 'react'
import { Zap, Check } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, Button } from '../UI'
import { purchaseCreditPack } from '../../services/stripe'
import { CREDIT_PACKS } from '../../config/pricing'

const CreditPacks = ({ onClose }) => {
  const [loadingPack, setLoadingPack] = useState(null)
  const [error, setError] = useState(null)

  const handlePurchase = async (packId) => {
    try {
      setError(null)
      setLoadingPack(packId)
      
      // Map pack IDs to the format expected by stripe service
      const packIdMap = {
        starter: 'credits100',
        standard: 'credits500',
        pro: 'credits1500',
        enterprise: 'credits5000',
      }
      
      await purchaseCreditPack(packIdMap[packId] || packId)
    } catch (err) {
      console.error('Failed to purchase credits:', err)
      setError(err.message)
      setLoadingPack(null)
    }
  }

  return (
    <Card padding="lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Buy Credits</CardTitle>
            <CardDescription>One-time purchases, never expire</CardDescription>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-dark-400 hover:text-dark-200 transition-colors"
            >
              ×
            </button>
          )}
        </div>
      </CardHeader>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {CREDIT_PACKS.map((pack) => (
          <div
            key={pack.id}
            className={`relative p-4 bg-dark-800 rounded-xl border transition-all ${
              pack.popular 
                ? 'border-accent-500/50 ring-1 ring-accent-500/20' 
                : 'border-dark-700 hover:border-dark-600'
            }`}
          >
            {/* Popular Badge */}
            {pack.popular && (
              <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                <span className="bg-accent-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5" />
                  Best Value
                </span>
              </div>
            )}

            {/* Discount Badge */}
            {pack.discount && (
              <div className="absolute -top-2 -right-2">
                <span className="bg-green-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  {pack.discount}
                </span>
              </div>
            )}

            <div className="text-center">
              {/* Pack Name */}
              <h4 className="font-medium text-sm mb-1">{pack.name}</h4>

              {/* Credits Amount */}
              <p className="text-2xl font-bold text-accent-400 mb-0.5">
                {pack.credits.toLocaleString()}
              </p>
              <p className="text-dark-500 text-xs mb-2">credits</p>

              {/* Price */}
              <div className="mb-3">
                <span className="text-lg font-bold">${pack.price / 100}</span>
                <p className="text-dark-500 text-[10px]">
                  ${pack.pricePerCredit.toFixed(3)}/credit
                </p>
              </div>

              {/* Buy Button */}
              <Button
                variant={pack.popular ? 'primary' : 'outline'}
                size="sm"
                fullWidth
                loading={loadingPack === pack.id}
                disabled={loadingPack !== null}
                onClick={() => handlePurchase(pack.id)}
              >
                Buy Now
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-dark-500 text-xs text-center mt-4">
        Purchased credits are added to your balance and never expire
      </p>
    </Card>
  )
}

export default CreditPacks
